import type { TFunction } from 'i18next'
import type { RecurrenceRule, Weekday } from '../db/models'
import { nthKey, weekdayName } from './describeRecurrence'
import { WORKDAYS } from './presets'

const shortWeekdayList = (days: readonly Weekday[], locale: string) =>
  [...new Set(days)].sort((a, b) => a - b).map(d => weekdayName(d, locale, 'short')).join(', ')

/** „24. Dez." / „Dec 24" — `Intl` kennt die Reihenfolge je Sprache. */
const shortYearDate = (month: number, day: number, locale: string) =>
  new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(new Date(2026, month - 1, day))

const isWorkdays = (days: readonly Weekday[]) => {
  const set = new Set(days)
  return set.size === WORKDAYS.length && WORKDAYS.every(d => set.has(d))
}

/**
 * Die **Kurzform** einer Wiederholung — der Text der **Marke** auf der Seite Wiederkehrend
 * („3× pro Woche", „Jeden Mo"). Wie {@link describeRecurrence} aus den Feldern erzeugt, nie aus
 * einem Preset. Schlüssel unter `recurrence.short.*`.
 */
export function shortRecurrenceLabel(rule: RecurrenceRule, t: TFunction, locale: string): string {
  switch (rule.type) {
    case 'quota':
      return t('recurrence.short.quota', { count: rule.count, period: t(`recurrence.period.${rule.period}`) })
    case 'after_done':
      return t('recurrence.short.afterDone', { span: t(`recurrence.span.${rule.unit}`, { count: rule.interval }) })
    case 'fixed':
      switch (rule.unit) {
        case 'day':
          return t('recurrence.short.fixedDay', { count: rule.interval })
        case 'week':
          if (rule.interval === 1 && isWorkdays(rule.weekdays)) return t('recurrence.short.weekdays')
          return t('recurrence.short.fixedWeek', { count: rule.interval, days: shortWeekdayList(rule.weekdays, locale) })
        case 'month':
          if (rule.nthWeekday) {
            return t('recurrence.short.fixedMonthNth', {
              count: rule.interval,
              nth: t(`recurrence.nth.${nthKey(rule.nthWeekday.nth)}`),
              weekday: weekdayName(rule.nthWeekday.weekday, locale, 'short'),
            })
          }
          if (rule.monthDay === 31) return t('recurrence.short.fixedMonthLast', { count: rule.interval })
          return t('recurrence.short.fixedMonth', { count: rule.interval, day: rule.monthDay })
        case 'year':
          return t('recurrence.short.fixedYear', { count: rule.interval, date: shortYearDate(rule.yearMonth, rule.yearDay, locale) })
      }
  }
}
